import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { Tab, Tabs } from "@/components/Tabs";
import { RoomDetails } from "@/features/hotels/components/RoomSelection/RoomDetails";
import type {
  HotelBookingRoomOption,
  HotelData,
} from "@/features/hotels/types/types";

type Props = {
  hotel: HotelData;
  rooms: HotelBookingRoomOption[] | null;
  onClose: () => void;
};

export const RoomDetailsDrawer = ({ hotel, rooms, onClose }: Props) => {
  const intl = useIntl();
  const [selectedRoomIndex, setSelectedRoomIndex] = useState<number>(0);

  const uniqueRooms = useMemo(() => {
    if (!rooms) return [];

    const seen = new Set();
    return rooms.filter((room) => {
      const identifier = room.name.trim().toLowerCase();
      if (seen.has(identifier)) {
        return false;
      }
      seen.add(identifier);
      return true;
    });
  }, [rooms]);

  const safeRoomIndex = Math.min(
    selectedRoomIndex,
    Math.max(0, uniqueRooms.length - 1),
  );

  return (
    <SwipeableDrawer
      open={Boolean(rooms?.length)}
      hasFullHeight
      onClose={onClose}
      backgroundColor="background"
      disableRestoreFocus
    >
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Room details",
          description: "Section heading for room details and description",
        })}
      />

      <DrawerContent>
        {uniqueRooms.length > 1 ? (
          <div className="mb-3">
            <Tabs<number>
              value={safeRoomIndex}
              onChange={(index) => {
                setSelectedRoomIndex(index);
              }}
            >
              {uniqueRooms.map((room, index) => (
                <Tab
                  key={room.name}
                  value={index}
                  label={intl.formatMessage(
                    {
                      defaultMessage: "Room {number}",
                      description:
                        "Label for individual rooms within a hotel booking, shown in a tab format when multiple rooms are available",
                    },
                    { number: index + 1 },
                  )}
                />
              ))}
            </Tabs>
          </div>
        ) : null}

        {uniqueRooms.length > 0 ? (
          <RoomDetails
            key={uniqueRooms[safeRoomIndex].name}
            option={uniqueRooms[safeRoomIndex]}
            hotelName={hotel.name}
          />
        ) : null}
      </DrawerContent>
    </SwipeableDrawer>
  );
};
